import React from 'react';
import { ArrowDownIcon, ArrowRightIcon, CircleDotIcon } from 'lucide-react';
import { MCP_EXAMPLE_STEPS, MCP_WAITLIST_PATH } from '../data/mcp';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import type { WaitlistPreviewState } from '../hooks/useMcpWaitlist';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { ExampleFrame } from '../components/examples/ExampleFrame';
import { McpWaitlistForm } from '../components/mcp/McpWaitlistForm';
function ExampleChain() {
  return <ExampleFrame title="Investment team · new submission" subtitle="Three agents, one draft for review" caption="Illustrative example of how agents could be combined. Not a list of available tools." data-id="element-1467" data-mp="2keotkkjg34gf">
      <ol className="flex flex-col" data-id="element-1468" data-mp="3vu40oh5dbcp9">
        {MCP_EXAMPLE_STEPS.map((step, index) => <li key={step.agent} className="flex flex-col" data-id="element-1469" data-mp="3gzmc547ch4zr" data-mpi={index}>
            <div className="rounded-xl border border-hairline-soft bg-surface px-3.5 py-3" data-id="element-1470" data-mp="3gz46xi2pfgfp">
              <div className="flex items-center justify-between gap-2" data-id="element-1471" data-mp="3lxe1bqglud09">
                <p className="text-sm font-semibold text-text-primary" data-id="element-1472" data-mp="3pzsim64ml4pf">{step.action}</p>
                <span className="font-mono text-[11px] text-lime" data-id="element-1473" data-mp="3uz5a546fyzyc">{step.agent}</span>
              </div>
              <p className="mt-1 text-[13px] text-text-secondary" data-id="element-1474" data-mp="2y9o4gp7gldwn">{step.result}</p>
            </div>
            {index < MCP_EXAMPLE_STEPS.length - 1 ? <span className="flex h-6 items-center justify-center text-text-tertiary" aria-hidden="true" data-id="element-1475" data-mp="328n6a0xw8ujv">
                <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-1476" data-mp="377zryid13vkv" />
              </span> : null}
          </li>)}
      </ol>
      <p className="mt-4 rounded-lg bg-violet-soft px-3.5 py-2.5 text-[13px] font-medium text-lime" data-id="element-1477" data-mp="2elos59x8xyp4">An analyst reviews the draft before anything moves forward.</p>
    </ExampleFrame>;
}
export function Mcp({
  waitlistPreview = 'live'
}: {
  waitlistPreview?: WaitlistPreviewState;
}) {
  usePageMeta({
    title: 'MCP Access Waitlist | DiligenceGPT™ Agents in Your Own Workflow',
    description: 'DiligenceGPT™ has set up a Model Context Protocol (MCP) server for access to its due diligence agents. Join the waitlist and tell us which workflow you want to connect first.',
    path: '/mcp',
    keywords: ['DiligenceGPT MCP', 'Model Context Protocol due diligence', 'due diligence AI agents API'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'MCP Access',
        item: canonicalUrl('/mcp')
      }]
    }]
  });
  return <main id="main" data-id="element-1478" data-mp="s5xu2q090mx8">
      <PageHero headingId="mcp-heading" breadcrumb="MCP Access" eyebrow="Developer access · Waitlist open" title="Use DiligenceGPT™ agents in your own workflow." description="We’ve set up a Model Context Protocol (MCP) server for access to DiligenceGPT™ agents. Join the waitlist and tell us which diligence workflow you want to connect first." actions={<CtaButton href={MCP_WAITLIST_PATH} variant="violet" size="xl" event="mcp_waitlist_cta_click" eventParams={{
      placement: 'mcp_hero'
    }} className="w-full sm:w-auto" data-id="element-1480" data-mp="ls1vgdphdcoe">
            Join the MCP waitlist
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1481" data-mp="1jdpp4qujxgw8" />
          </CtaButton>} aside={<div className="flex flex-col gap-3" data-id="element-1482" data-mp="14p32e42lhmuz">
            <div className="flex items-center gap-3 rounded-xl border border-night-line bg-night-panel px-4 py-3" data-id="element-1483" data-mp="19nwhg0xj5lx9">
              <CircleDotIcon className="h-4 w-4 shrink-0 text-[#4ADE80]" aria-hidden="true" data-id="element-1484" data-mp="1xcz4joaxo6z5" />
              <p className="text-sm text-night-text" data-id="element-1485" data-mp="225sagfi51lct">
                <span className="font-semibold" data-id="element-1486" data-mp="226jx30trc8qy">Status: Waitlist open.</span> <span className="text-night-muted" data-id="element-1487" data-mp="djpcudxbb3o4">Access isn’t available yet.</span>
              </p>
            </div>
            <ExampleChain data-id="element-1488" data-mp="9g14ht0uw41t" />
          </div>} data-id="element-1479" data-mp="s5601jmaux92" />

      <section id="waitlist" aria-labelledby="waitlist-heading" className="scroll-mt-24 border-b border-hairline-soft bg-surface" data-id="element-1489" data-mp="4hg33q9vbebs">
        <div className="mx-auto grid w-full max-w-[1100px] gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-14 lg:px-8 lg:py-24" data-id="element-1490" data-mp="3vqdd9qhf32ng">
          <div data-id="element-1491" data-mp="s7k4rs3hx405">
            <SectionHeading eyebrow="Waitlist" headingId="waitlist-heading" title="Join the MCP access waitlist." description="Tell us which workflow you want to connect first. We’ll use it to decide which agents to open up and email you when access is available." data-id="element-1492" data-mp="s94yxglwekxh" />
            <Reveal as="ul" className="mt-8 flex flex-col gap-3 text-[15px] text-text-secondary" data-id="element-1493" data-mp="1fzhbx5w037ux">
              <li className="flex gap-3" data-id="element-1494" data-mp="1b4ld9mw9dm8u">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" data-id="element-1495" data-mp="1iim75fr2mnwx" />
                Invest, Buy, Acquire, Select, Assure or Monitor — or something else.
              </li>
              <li className="flex gap-3" data-id="element-1496" data-mp="1sfqs5c96kdc5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" data-id="element-1497" data-mp="1si39pob10loo" />
                Agents prepare the work; your team still reviews and decides.
              </li>
            </Reveal>
          </div>
          <Reveal delay={0.05} data-id="element-1498" data-mp="26fi4z9nynnok">
            <McpWaitlistForm preview={waitlistPreview} data-id="element-1499" data-mp="26egsd0raqt4q" />
          </Reveal>
        </div>
      </section>
    </main>;
}